// MagicLinkTimeline — read-only timeline of one user's magic-link
// activity. Used in spec 44 admin-users detail drawer (30-day window).
//
// Renders rows for issued and consumed events, newest-first. Includes a
// "revoke" button on still-active rows that calls back to the parent.

import type { MagicLinkPurpose } from '../lib/notify-types';

export interface MagicLinkRecord {
  /** AuthMagicLinks.id */
  id: number;
  purpose: MagicLinkPurpose;
  issuedAt: string;
  expiresAt: string;
  /** ISO timestamp of consumption (null if unused). */
  consumedAt: string | null;
  /** ISO timestamp of explicit revocation (null if not revoked). */
  revokedAt: string | null;
  /** Set when the volunteer's IP / UA was captured on consume. */
  consumedFrom?: { ip?: string; userAgent?: string };
}

const PURPOSE_LABELS: Record<MagicLinkPurpose, string> = {
  'briefing-ack': 'Briefing acknowledgement',
  'duty-detail': 'Duty detail',
  callback: 'Post-event callback',
};

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

function statusOf(r: MagicLinkRecord): {
  label: string;
  className: string;
} {
  if (r.revokedAt) {
    return {
      label: 'Revoked',
      className: 'bg-stone-100 text-stone-600',
    };
  }
  if (r.consumedAt) {
    return {
      label: 'Consumed',
      className: 'bg-emerald-50 text-emerald-800',
    };
  }
  if (new Date(r.expiresAt).getTime() < Date.now()) {
    return {
      label: 'Expired',
      className: 'bg-stone-100 text-stone-500',
    };
  }
  return {
    label: 'Active',
    className: 'bg-amber-50 text-amber-900',
  };
}

export interface MagicLinkTimelineProps {
  records: MagicLinkRecord[];
  /** Called when the admin clicks Revoke on an active row. */
  onRevoke?: (id: number) => void | Promise<void>;
}

export function MagicLinkTimeline({ records, onRevoke }: MagicLinkTimelineProps) {
  if (records.length === 0) {
    return (
      <p className="text-sm text-stone-500">
        No magic links issued in the last 30 days.
      </p>
    );
  }

  const sorted = [...records].sort(
    (a, b) =>
      new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime(),
  );

  return (
    <ol className="divide-y divide-stone-100" data-component="magic-link-timeline">
      {sorted.map(r => {
        const s = statusOf(r);
        const revokable = !r.revokedAt && !r.consumedAt &&
          new Date(r.expiresAt).getTime() > Date.now();

        return (
          <li key={r.id} className="flex items-start gap-3 py-3">
            <span
              className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold ${s.className}`}
            >
              {s.label}
            </span>
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium text-stone-900">
                {PURPOSE_LABELS[r.purpose]}
              </div>
              <div className="text-xs text-stone-500">
                Issued {relativeTime(r.issuedAt)}
                {r.consumedAt && (
                  <> · consumed {relativeTime(r.consumedAt)}</>
                )}
                {r.revokedAt && (
                  <> · revoked {relativeTime(r.revokedAt)}</>
                )}
              </div>
              {r.consumedFrom?.ip && (
                <div className="text-xs text-stone-400 font-mono mt-0.5">
                  {r.consumedFrom.ip}
                </div>
              )}
            </div>
            {revokable && onRevoke && (
              <button
                type="button"
                onClick={() => onRevoke(r.id)}
                className="shrink-0 rounded-md border border-rose-300 px-2 py-0.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
              >
                Revoke
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
